import React from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useSortable } from '@dnd-kit/react/sortable';

import { Date } from '../date/date';
import { DateSelectionModal } from '../../features/process/components/date-selection.modal';
import { Step, StepStatus } from '../../app/entities/step';
import { DeleteDialog } from '../dialogs/DeleteDialog';
import { AssigneeSelector } from '../assignee-selector/assignee-selector';
import { StatusSelector } from '../../features/process/components/status-selector';
import { useCurrentMember } from '../../hooks/use-current-member';
import { KanbanCardAction } from '../../features/kanban/components/kanban-card-action';
import { KanbanItemCard } from '../../features/kanban/components/kanban-item-card';
import { uiStore } from '../../core/stores/ui-store';
import { ClipboardTick } from '../icons';
import { openModal } from '../../core/modal/open-modal';
import { asPlainDate, isPlainDate } from '../../core/services/plain-date';

type StepItemProps = {
  stepId: string;
  index: number;
  stageId: string;
};

export const StepItem: React.FC<StepItemProps> = observer(({ stepId, index, stageId }) => {
  const navigate = useNavigate();
  const step = Step.getOne(stepId);

  const { ref, isDragging } = useSortable({
    id: stepId,
    index,
    type: 'item',
    accept: 'item',
    group: stageId,
  });

  const currentMember = useCurrentMember();

  const isShootingDay = step.type === 'shootingDay';

  const handleDelete = () => {
    openModal(DeleteDialog, {
      title: 'Delete step',
      text: `Are you sure you want to delete ${step.name}?`,
      onSubmit: handleDelete,
    });
  };

  const handleOpenDate = () => {
    openModal(DateSelectionModal, {
      fieldName: isShootingDay ? 'startDate' : 'dueDate',
      value: (isShootingDay ? step.startDate : step.dueDate) ?? undefined,
      onSubmit: handleUpdateStep,
    });
  };

  const handleShowTasksPanel = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    if (!uiStore.isTaskPanelOpen) {
      uiStore.openTaskPanel(step._id);
    } else if (uiStore.taskPanelTargetId === step._id) {
      uiStore.closeTaskPanel();
    } else {
      uiStore.openTaskPanel(step._id);
    }
  };

  // `DateSelectionModal` hands back a raw `<input type="date">` value: a real calendar day, or
  // `null`/`''` when the native clear icon was used. This is the boundary where the `PlainDate`
  // is minted — clearing is a real, API-supported action, so an empty value goes through as
  // `null`; a non-empty but malformed value has no safe day to mint and is dropped in place.
  const handleUpdateStep = async (value: string | null) => {
    if (value && !isPlainDate(value)) return;

    const date = value ? asPlainDate(value) : null;

    step.update({ ...(isShootingDay ? { startDate: date, dueDate: date } : { dueDate: date }) });
  };

  const handleChangeStatus = async (newStatus: StepStatus) => {
    try {
      await step.update({ status: newStatus });
    } catch (e) {
      console.error(e);
    }
  };

  const goTo = () => navigate(`./steps/${step._id}`);

  return (
    <KanbanItemCard
      ref={ref}
      status={step.status}
      isDone={step.done}
      isDragging={isDragging}
      onClick={goTo}
      title={step.displayName}
      capitalizeTitle
      leading={
        <StatusSelector
          onChange={handleChangeStatus}
          statusMap={Step.StatusMapper}
          status={step.status}
          isViewOnly={currentMember?.role === 'guest'}
        />
      }
      trailing={
        <div onClick={(e) => e.stopPropagation()}>
          <AssigneeSelector
            isViewOnly={currentMember?.role === 'guest'}
            project={step.project!}
            selectedMemberId={step.assigneeId}
            onChange={(assigneeId) => step.update({ assigneeId })}
            iconOnly
          />
        </div>
      }
      footer={
        <>
          <Date date={isShootingDay ? step.startDate : step.dueDate} onClick={handleOpenDate} />

          <KanbanCardAction
            icon={<ClipboardTick />}
            onClick={handleShowTasksPanel}
            text={
              step.activeTasksCount ? `${step.completedTask}/${step.activeTasksCount}` : undefined
            }
          />
        </>
      }
    />
  );
});
